import { useCallback, useEffect, useRef, useState } from "react";

import { HttpApiService } from "@/shared/services/httpApi.service";

import { CardsService } from "../services/cardsService";
import type { CardTransaction } from "../types/card-movements.types";

export interface UseCardTransactionDetailArgs {
  cardId: string;
  transactionId: string | null;
}

export interface UseCardTransactionDetailResult {
  detail: CardTransaction | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Fetches a single card transaction detail whenever `transactionId` changes. A sequence ref guards against
 * out-of-order responses when the user opens several transactions quickly. Exposes `refetch` so the error
 * state can offer a retry. Mirrors the wallets movement-detail hook.
 */
export const useCardTransactionDetail = ({
  cardId,
  transactionId,
}: UseCardTransactionDetailArgs): UseCardTransactionDetailResult => {
  const [detail, setDetail] = useState<CardTransaction | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const seqRef = useRef(0);

  const fetchDetail = useCallback(async () => {
    if (!transactionId) {
      seqRef.current += 1;
      setDetail(null);
      setError(null);
      setIsLoading(false);
      return;
    }

    const seq = ++seqRef.current;
    setIsLoading(true);
    setError(null);
    setDetail(null);

    try {
      const response = await CardsService.getCardTransactionDetail(cardId, transactionId);
      if (seq !== seqRef.current) return;
      setDetail(response.data);
    } catch (err: unknown) {
      if (seq !== seqRef.current) return;
      setDetail(null);
      setError(HttpApiService.getErrorMessage(err) || "error");
    } finally {
      if (seq === seqRef.current) setIsLoading(false);
    }
  }, [cardId, transactionId]);

  useEffect(() => {
    void fetchDetail();
  }, [fetchDetail]);

  return { detail, isLoading, error, refetch: () => void fetchDetail() };
};
